// src/components/Common/ProductsFilter.js
import React from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Collapse,
  Grid,
  MenuItem,
  Chip,
  Button,
  IconButton,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Search,
  FilterList,
  ExpandLess,
  ExpandMore
} from '@mui/icons-material';

const ProductsFilter = ({
  searchTerm,
  setSearchTerm,
  selectedMainCategory,
  setSelectedMainCategory,
  selectedSubCategory,
  setSelectedSubCategory,
  stockFilter,
  setStockFilter,
  showFilters,
  setShowFilters,
  categories,
  filteredProductsCount,
  onResetFilters
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // RTL TextField styling
  const rtlTextFieldSx = {
    '& .MuiOutlinedInput-root': {
      '& .MuiOutlinedInput-notchedOutline legend': {
        textAlign: 'right',
        transformOrigin: 'top right',
        right: 12,
        left: 'auto'
      }
    },
    '& .MuiInputBase-input': {
      textAlign: 'right',
      direction: 'rtl',
      fontWeight: 'bold'
    }
  };

  // Get main categories (categories without parent)
  const getMainCategories = () => {
    return categories.filter(category => !category.parent_id);
  };

  // Get subcategories for a specific main category
  const getSubCategories = (mainCategoryId) => {
    if (!mainCategoryId) return [];
    return categories.filter(category => category.parent_id === mainCategoryId);
  };

  // Handle main category change and reset subcategory
  const handleMainCategoryChange = (mainCategoryId) => {
    setSelectedMainCategory(mainCategoryId);
    setSelectedSubCategory(''); // Reset subcategory when main category changes
  };

  // Get stock filter display text
  const getStockFilterDisplayText = (filter) => {
    switch (filter) {
      case 'in-stock': return 'متوفر';
      case 'low-stock': return 'منخفض';
      case 'out-of-stock': return 'نفد';
      default: return '';
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      {/* Search Bar */}
      <TextField
        fullWidth
        placeholder="البحث في المنتجات..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search sx={{ ml: 1 }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => setShowFilters(!showFilters)}>
                <FilterList />
                {showFilters ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </InputAdornment>
          )
        }}
        sx={{ 
          mb: 2,
          ...rtlTextFieldSx
        }}
      />

      {/* Collapsible Filters */}
      <Collapse in={showFilters}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {/* Main Category Filter */}
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              label="الفئة الرئيسية"
              value={selectedMainCategory}
              onChange={(e) => handleMainCategoryChange(e.target.value)}
              size="small"
              InputLabelProps={{
                sx: {
                  fontWeight: 'bold',
                  left: 'auto',
                  '&:not(.MuiInputLabel-shrink)': {
                    right: 45,   
                  },
                  '&.MuiInputLabel-shrink': {
                    right: 1,
                    transform: 'translateX(7px) translateY(-7px) scale(0.75)',
                  },
                }
              }}
              sx={rtlTextFieldSx}
            >
              <MenuItem value="" sx={{ flexDirection: 'row-reverse' }}>
                جميع الفئات الرئيسية
              </MenuItem>
              {getMainCategories().map((category) => (
                <MenuItem 
                  key={category.id} 
                  value={category.id}
                  sx={{ flexDirection: 'row-reverse', fontWeight: 'bold' }}
                >
                  {category.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          
          {/* Sub Category Filter */}
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              label="الفئة الفرعية"
              value={selectedSubCategory}
              onChange={(e) => setSelectedSubCategory(e.target.value)}
              size="small"
              disabled={!selectedMainCategory}
              InputLabelProps={{
                sx: {
                  fontWeight: 'bold',
                  left: 'auto',
                  '&:not(.MuiInputLabel-shrink)': {
                    right: 45,   
                  },
                  '&.MuiInputLabel-shrink': {
                    right: 1,
                    transform: 'translateX(7px) translateY(-7px) scale(0.75)',
                  },
                }
              }}
              sx={rtlTextFieldSx}
            >
              <MenuItem value="" sx={{ flexDirection: 'row-reverse' }}>
                جميع الفئات الفرعية
              </MenuItem>
              {getSubCategories(selectedMainCategory).map((category) => (
                <MenuItem 
                  key={category.id} 
                  value={category.id}
                  sx={{ flexDirection: 'row-reverse', fontWeight: 'bold' }}
                >
                  {category.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          
          {/* Stock Filter */}
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              label="حالة المخزون"
              value={stockFilter}
              onChange={(e) => setStockFilter(e.target.value)}
              size="small"
              InputLabelProps={{
                sx: {
                  fontWeight: 'bold',
                  textAlign: 'right',
                  transformOrigin: 'top right',
                  right: 26,
                  left: 'auto'
                }
              }}
              sx={rtlTextFieldSx}
            >
              <MenuItem value="all" sx={{ flexDirection: 'row-reverse', fontWeight: 'bold' }}>
                جميع المنتجات
              </MenuItem>
              <MenuItem value="in-stock" sx={{ flexDirection: 'row-reverse', fontWeight: 'bold' }}>
                متوفر
              </MenuItem>
              <MenuItem value="low-stock" sx={{ flexDirection: 'row-reverse', fontWeight: 'bold' }}>
                مخزون منخفض
              </MenuItem>
              <MenuItem value="out-of-stock" sx={{ flexDirection: 'row-reverse', fontWeight: 'bold' }}>
                نفد المخزون
              </MenuItem>
            </TextField>
          </Grid>
        </Grid>
        
        {/* Active Filters Display */}
        {(searchTerm || selectedMainCategory || selectedSubCategory || stockFilter !== 'all') && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexDirection: isMobile ? 'column' : 'row' }}>
            <Button variant="outlined" onClick={onResetFilters} size="small">
              إعادة تعيين الفلاتر
            </Button>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {searchTerm && (
                <Chip 
                  label={`البحث: ${searchTerm}`} 
                  onDelete={() => setSearchTerm('')} 
                  size="small"
                />
              )}
              {selectedMainCategory && (
                <Chip 
                  label={`الفئة الرئيسية: ${categories.find(c => c.id === selectedMainCategory)?.name || ''}`} 
                  onDelete={() => handleMainCategoryChange('')} 
                  size="small"
                />
              )}
              {selectedSubCategory && (
                <Chip 
                  label={`الفئة الفرعية: ${categories.find(c => c.id === selectedSubCategory)?.name || ''}`} 
                  onDelete={() => setSelectedSubCategory('')} 
                  size="small"
                />
              )}
              {stockFilter !== 'all' && (
                <Chip 
                  label={`المخزون: ${getStockFilterDisplayText(stockFilter)}`} 
                  onDelete={() => setStockFilter('all')} 
                  size="small"
                />
              )}
            </Box>
          </Box>
        )}
      </Collapse>
    </Box>
  );
};

export default ProductsFilter;